import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { formatUrl } from '../shared/utils/format-url-util';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { HttpRequestService } from '../shared/services/http-request.service';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { NotificationService } from '../shared/services/notification.service';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class TimesheetService {
  constructor(
    private httpClient: HttpRequestService,
    private http: HttpClient,
    private errorHandler: GlobalErrorHandlerService,
    private notificationService: NotificationService
  ) { }

  getTimeoffServiceCodes(
    program_code: string,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_TIME_OFF_SERVICE_CODES, {
      program_code,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getTimeoffServiceCodesByConsumer(
    program_code: string,
    consumerId: number,
    providerId: number
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_TIME_OFF_SERVICE_CODES_BY_CONSUMER, {
      program_code,
    });
    return this.httpClient.post(url, {
      programCode: program_code,
      source: "sicktime",
      consumerId: consumerId,
      providerId: providerId
    }).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  updateStatusConsumerTimesheet(payload: any, status: string): Observable<any> {
    const statusName = (status === "GP") ? 'approved' : 'denied';
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.UPDATE_STATUS_CONSUMER_TIMESHEET, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Timesheet ${statusName} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  saveAdjudicationDetails(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.SAVE_ADJUDICATIONS, payload, false)
      .pipe(
        tap(() => {
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  downloadPaystub(event: any, programCode: string) {
    this.getPayStub(event.event.checkId, event.event.chequeNumber, event.event.providerPplId, programCode).subscribe((response: HttpResponse<Blob>) => {
      if (response.body) {
        const blob = new Blob([response.body], { type: response.body.type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${event.event.chequeNumber}_Paystub`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    });
  }

  getproviderTsDenialReasons(
    programCode: string,
    timesheetdetailID: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_TS_DENIAL_REASONS, {
      programCode,
      timesheetdetailID
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getConsumerList(
    programCode: string,
    providerId: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_CONSUMER_LIST, {
      programCode,
      providerId
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }
  getConsumerListByPPLId(
    payload: any,
    isAfterUpdate = false
  ): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.GET_CONSUMER_LIST_BY_PPLID, payload, isAfterUpdate)
      .pipe(
        tap(() => {
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getPayPeriod(
    programCode: string,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_PAY_PERIOD, {
      programCode
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      map((data: any) => {
        data.responsedata?.sort((a: any, b: any) =>
          new Date(b.payrollStartDate).getTime() - new Date(a.payrollStartDate).getTime()
        );
        return data;
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getServicePeriod(
    programCode: string,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_SERVICE_PERIOD, { programCode });

    return this.httpClient.get(url, isAfterUpdate).pipe(
      map((data: any) => {
        data.responsedata?.sort((a: any, b: any) =>
          new Date(b.workWeekStartDate).getTime() - new Date(a.workWeekStartDate).getTime()
        );
        return data;
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getServiceCodes(
    programCode: string,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_TS_SERVICE_CODE, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getProcessCodes(
    programCode: string,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_TS_PROCESS_CODE, {
      programCode,
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getServiceCodesByConsumer(
    payload: any,
  ): Observable<any> {
    return this.httpClient.post(ENDPOINTS.CLAIMS.GET_SERVICE_CODE_BY_CONSUMER, payload, true)
      .pipe(
        tap(() => {
          // this.notificationService.success(
          //   'Success',
          //   'Timesheet updated Successfully.'
          // );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getAuthDetails(
    programCode: string,
    payrollId: any,
    isAfterUpdate = false
  ): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_AUTH_DETAILS, {
      programCode, payrollId
    });
    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getConsumerTsDetails(
    type: string,
    programCode: string,
    participantId: any,
    providerId: any,
    fromDate: any,
    toDate: any,
    isAfterUpdate = false
  ): Observable<any> {
    const endpoint = (type === 'processed' ? ENDPOINTS.CLAIMS.GET_CONSUMER_PROCESSED_TIMESHEET_DETAILS : ENDPOINTS.CLAIMS.GET_CONSUMER_IP_TIMESHEET_DETAILS)
    const url = formatUrl(endpoint, {
      programCode,
      participantId,
      providerId,
      fromDate,
      toDate
    });

    return this.httpClient.get(url, isAfterUpdate).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getConsumerPayrollDetails(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.GET_CONS_TS_DETAILS, payload, true)
      .pipe(
        tap(() => {
          // this.notificationService.success(
          //   'Success',
          //   'Timesheet updated Successfully.'
          // );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getTimesheet(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.GET_TIMESHEET, payload, true)
      .pipe(
        tap(() => {
          // this.notificationService.success(
          //   'Success',
          //   'Timesheet updated Successfully.'
          // );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  checkMealBreak(programCode: string, payload: any): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.MEAL_BREAK_PROMPT, { programCode });
    return this.httpClient
      .post(url, payload)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  saveTimesheet(payload: any, action: string): Observable<any> {
    if (payload && payload.serviceLines.length) {
      payload.serviceLines = payload.serviceLines.filter((e: any) => e.selected && e.serviceTimeIn && e.serviceTimeOut);
    }
    const actionPerformed = action.toLowerCase() === 'edit' ? 'updated' : 'added';
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.SAVE_TIMESHEET, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Timesheet ${actionPerformed} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  moveBackTimesheet(programCode: string, payload: any): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.MOVEBACK_TIMESHEET, {
      programCode
    });
    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Timesheet moved back successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  deleteTimesheet(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.DELETE_TIMESHEETS, payload, true)
      .pipe(
        tap((response: any) => {
          this.notificationService.success(
            'Success',
            response?.responsedata?.message ?? 'Timesheets deleted successfully.'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  saveTimeOff(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.SAVE_TIMEOFF, payload, true)
      .pipe(
        tap((data: any) => {
          if (data.status === 200) {
            const response = data.responsedata || {};
            const errors = [];
            let message = '';
            if (response && Array.isArray(response)) {
              response.forEach((item: any) => {
                if (item.status === 'FAILURE') {
                  errors.push(item.error);
                  message += `${item.error}\n`;
                }
              });
            }

            if (errors.length) {
              data.status = 400; // Set status to 400 if there are any failures in the response
            } else {
              this.notificationService.success(
                'Success',
                'Time off added successfully.'
              );
            }
          }
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getProviderTsDetails(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.GET_PA_TS_DETAILS, payload, true)
      .pipe(
        tap(() => {
          // this.notificationService.success(
          //   'Success',
          //   'Timesheet updated Successfully.'
          // );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getTimeOffTimesheets(programCode: string, providerID: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_VIEW_TIMEOFF, {
      programCode, providerID
    });
    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getAuthTimesheetUtilization(programCode: string, authId: string, participantId: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_AUTH_TS_UTILIZATION, {
      programCode, participantId, authId
    });

    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getAuthUtilization(programCode: string, participantId: string, authId: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_AUTH_UTILIZATION, {
      programCode, participantId, authId
    });
    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  updateTimesheetAdjudication(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.CLAIMS.UPDATE_TIMESHEET_ADJ, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Timesheet Adjudicated successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getPayStub(checkId: string, checkNo: string, empId: string, programCode: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders({
      'content-type': 'application/json'
    })

    const payload = {
      "programCode": programCode,
      "providerId": empId,
      "payStubNo": checkNo,
      "checkId": checkId,
      "checkStubShowEmployer": true,
      "showConsumersOnCheck": true,
      "disallowOvertimeOnCheckstub": true,
      "showPayPeriod": true,
      "showRate": true
    };

    return this.http.post(ENDPOINTS.PAY_API.GET_PAYSTUBS, payload, { headers: headers, responseType: 'blob', observe: 'response' });
  }

  /**
   * Deny timesheet lines. Payload: { timesheetLineIds: number[], status: 'D', remarks: string }.
   * Used by the Good To Pay Timesheets list for both Deny and Deny All — unlike the pend-management
   * deny endpoints, this one takes no process codes.
   */
  denyTimesheet(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.DENY_TIMESHEET, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success('Success', 'Timesheet denied successfully.');
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  denyOverrideTimesheet(endpointUrl: string, programCode: string, payload: any, placeholder: string): Observable<any> {
    const url = formatUrl(endpointUrl, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Timesheet ${placeholder} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  adjustVoidTimesheet(endpointUrl: string, programCode: string, payload: any, placeholder: string): Observable<any> {
    const url = formatUrl(endpointUrl, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        switchMap((response: any) => {
          if (response?.status == 409 && response?.responsedata == "TimesheetAdjust error") {
            return throwError(() => response);
          }
          this.notificationService.success(
            'Success',
            `${placeholder} successfully.`
          );
          return of(response);
        }),
        catchError((err) => {
          if (err?.error?.responsedata == "TimesheetAdjust error") {
            return throwError(() => err);
          }
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getBillingDetails(billingDetails: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.BILLING_TIMESHEET_DETAILS, { programCode });

    return this.httpClient
      .post(url, billingDetails, true)
      .pipe(
        tap(() => {
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  adjustBillings(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.BILLING_ADJUSTMENT, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Billings adjusted successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  saveMassBatch(programCode: string, payload: object): Observable<any> {
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.SAVE_BATCH, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            'Bulk adjustment batch saved successfully.'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  adjustInvoice(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.INVOICE_ADJUST, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            'Invoice adjusted successfully.'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  voidInvoice(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.INVOICE_VOID, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            'Invoice voided successfully.'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  invoiceVoidALL(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.INVOICE_VOID_ALL, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            'Invoice voided successfully.'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  invoiceAdjustALL(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.INVOICE_ADJUST_ALL, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            'Invoice adjusted successfully.'
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  adjustPurchase(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.PURCHASE_ADJUST, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap((res: any) => {
          if (res?.responsedata !== 'failure') {
            this.notificationService.success(
              'Success',
              'Purchase adjusted successfully.'
            );
          }
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  adjustVoidPurchase(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.VOID_PURCHASE_ADJUST, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap((res: any) => {
          if (res?.responsedata !== 'failure') {
            this.notificationService.success(
              'Success',
              'Purchase voided successfully.'
            );
          }
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }
  getDerivedElements(timesheetId: string | number): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.GET_DERIVED_ELEMENTS, { timesheetId });
    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  adjustVoidAllPurchase(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.CLAIMS.VOID_ALL_PURCHASE_ADJUST, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap((res: any) => {
          if (res?.responsedata !== 'failure') {
            this.notificationService.success(
              'Success',
              'Purchase voided successfully.'
            );
          }
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }
}
